import { oauthAuth } from "../standardAuthPatterns";
import { defineConnector } from "../types";
import { urlToPattern } from "../urlToPattern";
import logo from "./logo.svg";

export const epicFhir = defineConnector({
  identifier: "epicFhir",
  name: "Epic FHIR",
  tagLine: "Clinical and patient data through FHIR APIs",
  keywords:
    "ehr healthcare hl7 smart population health electronic health record",
  logo,
  tags: ["data"],
  protocol: "http",
  defaultUrlPattern: ({ fhirBaseUrl }) => urlToPattern(fhirBaseUrl),
  auth: oauthAuth,
  steps: [
    {
      title: "Register a backend application",
      items: [
        {
          kind: "markdown",
          content:
            "Create a Backend Systems application in [Epic on FHIR](https://fhir.epic.com), enable OAuth 2.0, and select the APIs it needs. Host your public signing key at a JWK Set URL and register that URL with Epic. Ask the healthcare organization’s Epic administrator to configure the JWK Set URL and map your client ID to a background user. See [Epic’s backend OAuth guide](https://fhir.epic.com/Documentation?docId=oauth2&section=BackendOAuth2Guide).",
        },
      ],
    },
    {
      title: "Enter your endpoints and credentials",
      items: [
        {
          kind: "baseUrl",
          label: "FHIR R4 base URL",
          placeholder:
            "https://fhir.epic.com/interconnect-fhir-oauth/api/FHIR/R4",
          required: true,
          helperText: "The healthcare organization’s FHIR R4 endpoint.",
          target: { kind: "configuration", field: "fhirBaseUrl" },
        },
        {
          kind: "fullUrl",
          label: "Token URL",
          placeholder:
            "https://fhir.epic.com/interconnect-fhir-oauth/oauth2/token",
          required: true,
          helperText:
            "Use token_endpoint from the FHIR base URL’s /.well-known/smart-configuration response, or ask the organization’s Epic administrator.",
          target: { kind: "configuration", field: "tokenUrl" },
        },
        {
          kind: "input",
          label: "Client ID",
          required: true,
          helperText:
            "Use the client ID for the environment you are connecting to.",
          target: { kind: "oauthConnection", field: "clientId" },
        },
        {
          kind: "file",
          label: "Private key",
          accept: ".pem,.key",
          required: true,
          helperText:
            "Upload the P-256 or P-384 EC private key matching the registered public key, in PEM format.",
          target: { kind: "oauthConnection", field: "clientSecret" },
        },
        {
          kind: "input",
          label: "Key ID",
          helperText:
            "Enter the signing key’s kid from the registered JWK Set, if present.",
          target: { kind: "configuration", field: "keyId" },
        },
      ],
    },
  ],
  oauth: {
    grantType: () => "client_credentials",
    clientCredentialsFrom: "db",
    tokenEndpoint: ({ tokenUrl }) => tokenUrl,
    tokenEndpointAuthMethod: () => "private_key_jwt",
    clientAssertion: ({ tokenUrl, keyId }) => ({
      audience: tokenUrl,
      keyId: keyId || undefined,
    }),
    scopes: () => [],
  },
  connectionTest: {
    protocol: "http",
    request: ({ fhirBaseUrl }) => ({
      url: `${fhirBaseUrl.replace(/\/+$/, "")}/metadata`,
      headers: { Accept: "application/fhir+json" },
      expectedStatus: 200,
      expectedBodyContains: '"CapabilityStatement"',
    }),
  },
  usageHint: ({ fhirBaseUrl }) =>
    `Use ${fhirBaseUrl} for Epic FHIR R4 requests. Access tokens are renewed automatically. Available resources and operations depend on the APIs enabled for the application and its background user. The connection test reads server capabilities, not patient records.`,
});
